import { computed, type ComputedRef, ref } from 'vue';

import { ELEVATIONS } from '@/constants/elevations';

export const SnackbarStatus = {
  SUCCESS: 'success',
  ERROR: 'error',
  DEFAULT: 'default'
} as const;

const Icon = {
  [SnackbarStatus.SUCCESS]: '$success',
  [SnackbarStatus.ERROR]: '$clear'
} as const;

const SnackbarLocation = {
  TOP_RIGHT: 'top right',
  BOTTOM_RIGHT: 'bottom right'
} as const;

const ColorOptions = {
  SURFACE: 'surface',
  SUCCESS: 'success',
  ERROR: 'error'
} as const;

type StatusTypes = (typeof SnackbarStatus)[keyof typeof SnackbarStatus];
type LocationTypes = (typeof SnackbarLocation)[keyof typeof SnackbarLocation];
type SnackbarBackgroundType = typeof ColorOptions.SURFACE | undefined;
type IconColorTypes = typeof ColorOptions.SUCCESS | typeof ColorOptions.ERROR;

type IconTypes = {
  color: IconColorTypes;
  icon: (typeof Icon)[keyof typeof Icon];
};

interface ShowSnackbarOptions {
  message: string;
  status?: StatusTypes;
  color?: SnackbarBackgroundType;
  timeout?: number;
}

const DEFAULT_TIMEOUT = 3000; // default 3 sec
const STATUS_CLASSES = 'text-body-1';

const isSnackbarVisible = ref<boolean>(false);
const message = ref<string>('');
const status = ref<StatusTypes>(SnackbarStatus.DEFAULT);
const customTimeout = ref<number | undefined>(undefined);

interface SnackbarMessage {
  isSnackbarVisible: typeof isSnackbarVisible;
  message: typeof message;
  status: typeof status;
  color: ComputedRef<SnackbarBackgroundType | undefined>;
  hasSnackbarCloseBtn: ComputedRef<boolean>;
  isToast: ComputedRef<boolean>;
  location: ComputedRef<LocationTypes>;
  icon: ComputedRef<IconTypes | undefined>;
  customClasses: ComputedRef<string | undefined>;
  elevationClass: ComputedRef<string>;
  timeout: ComputedRef<number>;
  showSnackbar: (options: ShowSnackbarOptions) => void;
  closeSnackbar: () => void;
}

/**
 * Composable for non-blocking, transient feedback notifications. Supports two distinct modes:
 *
 * **Toast** (status mode: SUCCESS/ERROR)
 * - Visual: White surface background, top-right corner, icon indicator
 * - Behavior: Auto-dismisses after 3s
 * - Elevation: 3 (more prominent, catches attention)
 * - Use for: Brief feedback (e.g., "Copied to clipboard", "Error uploading file")
 *
 * **Snackbar** (default mode)
 * - Visual: Gray surface-variant background, bottom-right corner, close button
 * - Behavior: Remains open until user dismisses or explicit timeout is set
 * - Elevation: 1 (subtle, secondary position)
 * - Use for: Persistent messages requiring acknowledgement (e.g., "New version available")
 *
 * @returns SnackbarMessage object with methods and state for managing notifications
 *
 * @example Toast (auto-hides):
 * const { showSnackbar } = useSnackbar();
 * showSnackbar({ message: 'Saved', status: SnackbarStatus.SUCCESS });
 *
 * @example Snackbar (persistent):
 * showSnackbar({ message: 'Update available', status: SnackbarStatus.DEFAULT });
 */
export const useSnackbar = (): SnackbarMessage => {
  const showSnackbar = ({
    status: customStatus = SnackbarStatus.DEFAULT, // used to determine if we are using the status or default version
    message: msg,
    timeout
  }: ShowSnackbarOptions) => {
    message.value = msg;
    status.value = customStatus;
    customTimeout.value = timeout;
    isSnackbarVisible.value = true;
  };

  const closeSnackbar = () => {
    isSnackbarVisible.value = false;
  };

  const isToast = computed(() => status.value !== SnackbarStatus.DEFAULT);

  const hasSnackbarCloseBtn = computed(() => !isToast.value);

  const icon = computed<IconTypes | undefined>(() => {
    switch (status.value) {
      case SnackbarStatus.SUCCESS:
        return {
          color: ColorOptions.SUCCESS,
          icon: Icon[SnackbarStatus.SUCCESS]
        };
      case SnackbarStatus.ERROR:
        return {
          color: ColorOptions.ERROR,
          icon: Icon[SnackbarStatus.ERROR]
        };
      default:
        return undefined;
    }
  });

  const color = computed<SnackbarBackgroundType | undefined>(() => {
    if (isToast.value) {
      return ColorOptions.SURFACE;
    }

    return undefined;
  });

  const location = computed<LocationTypes>(() => {
    // Toast (status mode) uses top-right; snackbar (default) uses bottom-right
    if (isToast.value) {
      return SnackbarLocation.TOP_RIGHT;
    }

    return SnackbarLocation.BOTTOM_RIGHT;
  });

  const customClasses = computed<string | undefined>(() => {
    // Toast (status) versions increase the font-size
    if (isToast.value) {
      return STATUS_CLASSES;
    }

    return undefined;
  });

  const timeout = computed<number>(() => {
    // Snackbar (non-toast) stays open unless otherwise indicated
    if (!isToast.value) {
      return customTimeout.value || -1;
    }

    return customTimeout.value || DEFAULT_TIMEOUT;
  });

  // v-snackbar has no `elevation` prop — its shadow is driven by an elevation
  // utility class on the content element (via `content-class`). Toast (status
  // mode) is more prominent than the default snackbar.
  const elevationClass = computed<string>(() => {
    const level = isToast.value
      ? ELEVATIONS.snackbarToast
      : ELEVATIONS.snackbar;
    return `elevation-${level}`;
  });

  return {
    isSnackbarVisible,
    hasSnackbarCloseBtn,
    isToast,
    message,
    status,
    color,
    location,
    timeout,
    elevationClass,
    icon,
    customClasses,
    showSnackbar,
    closeSnackbar
  };
};
